import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { BookOpen, FileText, MessageSquare, Mail, Eye, ArrowRight, CheckCircle, History, ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const getExercises = (t) => [
  {
    id: "schema_journal",
    name: t("schemaEx.journal.name"),
    description: t("schemaEx.journal.desc"),
    icon: FileText,
    color: "bg-indigo-50 text-indigo-600",
    prompts: [
      { key: "situation", label: t("schemaEx.journal.situation"), placeholder: t("schemaEx.journal.situationPh") },
      { key: "emotion", label: t("schemaEx.journal.emotion"), placeholder: t("schemaEx.journal.emotionPh") },
      { key: "thoughts", label: t("schemaEx.journal.thoughts"), placeholder: t("schemaEx.journal.thoughtsPh") },
      { key: "schema", label: t("schemaEx.journal.schema"), placeholder: t("schemaEx.journal.schemaPh") },
      { key: "reaction", label: t("schemaEx.journal.reaction"), placeholder: t("schemaEx.journal.reactionPh") },
    ],
  },
  {
    id: "memo_flash",
    name: t("schemaEx.memo.name"),
    description: t("schemaEx.memo.desc"),
    icon: BookOpen,
    color: "bg-amber-50 text-amber-600",
    prompts: [
      { key: "schema_voice", label: t("schemaEx.memo.voice"), placeholder: t("schemaEx.memo.voicePh") },
      { key: "why_schema", label: t("schemaEx.memo.why"), placeholder: t("schemaEx.memo.whyPh") },
      { key: "realistic", label: t("schemaEx.memo.realistic"), placeholder: t("schemaEx.memo.realisticPh") },
      { key: "action", label: t("schemaEx.memo.action"), placeholder: t("schemaEx.memo.actionPh") },
    ],
  },
  {
    id: "inner_dialogue",
    name: t("schemaEx.dialogue.name"),
    description: t("schemaEx.dialogue.desc"),
    icon: MessageSquare,
    color: "bg-teal-50 text-teal-600",
    prompts: [
      { key: "schema_side", label: t("schemaEx.dialogue.schemaSide"), placeholder: t("schemaEx.dialogue.schemaSidePh") },
      { key: "healthy_side", label: t("schemaEx.dialogue.healthySide"), placeholder: t("schemaEx.dialogue.healthySidePh") },
      { key: "schema_reply", label: t("schemaEx.dialogue.schemaReply"), placeholder: t("schemaEx.dialogue.schemaReplyPh") },
      { key: "healthy_final", label: t("schemaEx.dialogue.healthyFinal"), placeholder: t("schemaEx.dialogue.healthyFinalPh") },
    ],
  },
  {
    id: "vulnerable_child_letter",
    name: t("schemaEx.letter.name"),
    description: t("schemaEx.letter.desc"),
    icon: Mail,
    color: "bg-rose-50 text-rose-600",
    prompts: [
      { key: "letter", label: t("schemaEx.letter.letter"), placeholder: t("schemaEx.letter.letterPh") },
      { key: "child_response", label: t("schemaEx.letter.response"), placeholder: t("schemaEx.letter.responsePh") },
    ],
  },
  {
    id: "imagery_reparenting",
    name: t("schemaEx.imagery.name"),
    description: t("schemaEx.imagery.desc"),
    icon: Eye,
    color: "bg-purple-50 text-purple-600",
    prompts: [
      { key: "childhood_scene", label: t("schemaEx.imagery.scene"), placeholder: t("schemaEx.imagery.scenePh") },
      { key: "adult_intervention", label: t("schemaEx.imagery.intervention"), placeholder: t("schemaEx.imagery.interventionPh") },
      { key: "transformed_scene", label: t("schemaEx.imagery.transformed"), placeholder: t("schemaEx.imagery.transformedPh") },
      { key: "feeling_after", label: t("schemaEx.imagery.feeling"), placeholder: t("schemaEx.imagery.feelingPh") },
    ],
  },
];

const SchemaExercisesPage = ({ user, logout }) => {
  const { t } = useI18n();
  const EXERCISES = getExercises(t);
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [entries, setEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/schema-exercises`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSession = async () => {
    if (!selectedExercise) return;
    const filled = Object.values(responses).some(v => v.trim());
    if (!filled) { toast.error(t("exerciseForm.fillOne")); return; }
    try {
      const res = await axios.post(`${API}/schema-exercises`, {
        exercise_type: selectedExercise.id,
        responses,
      }, { withCredentials: true });
      toast.success(t("exerciseForm.saved"));
      if (user?.role === "patient") promptShare(res.data, "schema_exercises");
      setSelectedExercise(null);
      setResponses({});
      fetchEntries();
    } catch (e) { toast.error(t("exerciseForm.saveError")); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="schema-exercises-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("schemaEx.pageTitle")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("schemaEx.pageSubtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("exerciseForm.sessions")}
            </span>
          )}
        </div>

        {selectedExercise ? (
          <Card className="max-w-2xl mx-auto" data-testid="schema-exercise-form">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedExercise.icon className="w-5 h-5" />
                {selectedExercise.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{selectedExercise.description}</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {selectedExercise.prompts.map((prompt) => (
                <div key={prompt.key} className="space-y-1.5">
                  <label className="text-sm font-medium">{prompt.label}</label>
                  <Textarea
                    placeholder={prompt.placeholder}
                    value={responses[prompt.key] || ""}
                    onChange={(e) => setResponses(prev => ({ ...prev, [prompt.key]: e.target.value }))}
                    rows={prompt.key === "letter" ? 6 : 3}
                    data-testid={`input-${prompt.key}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button onClick={saveSession} className="flex-1" data-testid="save-exercise-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> {t("exerciseForm.save")}
                </Button>
                <Button variant="outline" onClick={() => { setSelectedExercise(null); setResponses({}); }}>
                  {t("exerciseForm.back")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {EXERCISES.map((exercise) => (
                <Card key={exercise.id} className="card-hover cursor-pointer" onClick={() => setSelectedExercise(exercise)} data-testid={`card-${exercise.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${exercise.color} flex items-center justify-center mb-3 sm:mb-4`}>
                      <exercise.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="font-heading text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 break-words">{exercise.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">{exercise.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {entries.length > 0 && (
              <div>
                <Button variant="ghost" className="mb-3 text-sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
                  {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {t("exerciseForm.history")} ({entries.length})
                </Button>
                {showHistory && (
                  <div className="space-y-3">
                    {entries.slice(0, 10).map((entry, i) => {
                      const ex = EXERCISES.find(e => e.id === entry.exercise_type);
                      return (
                        <Card key={i} className="bg-muted/30">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-sm">{ex?.name || entry.exercise_type}</span>
                              <span className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleDateString("fr-FR")}</span>
                            </div>
                            {entry.responses && Object.entries(entry.responses).slice(0, 2).map(([k, v]) => (
                              <p key={k} className="text-xs text-muted-foreground truncate">{v}</p>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default SchemaExercisesPage;
